import React, { useContext, useState, useEffect, useMemo, useRef } from "react";
import { Row, Col } from "react-bootstrap";
import SharedMainSelectableClientGrid from "shared/components/grids/clientGrid/SharedMainSelectableClientGrid";
import { AdminExportBatchHistoryForm } from "./AdminExportBatchHistoryForm";

import { UserContext } from "shared/context/UserContext";
import Accordion from "../../../components/Accordion";

import ErrorHandler from "../../../components/core/ErrorHandler";
import { IsNullOrWhitespace } from "shared/helpers/StringHelpers";
import { StringToDate, GetISODate } from "shared/helpers/DateHelpers";

import { DetailsModal } from "../../../components/grid/DetailsModal";
import { ZIndexContext } from "@progress/kendo-react-common";
import {
  DefaultColumnWidth,
  DefaultDateColumnWidth,
  DefaultBooleanOrShortWidthColumnWidth,
} from "../../../components/grid/Kendo/KendoGridAndColumnConstants";
import { GridColumn } from "@progress/kendo-react-grid";
import useGridDataStateResetProcessing from "shared/hooks/useGridDataStateResetProcessing";
import useGridButtons from "shared/components/grids/hooks/useGridButtons";
import { IsNullOrEmpty } from "shared/helpers/NullEmptyHelpers";
import { IsExportBatchRequestReadyToDownload } from "shared/helpers/ExportBatchStatusHelper";
import {
  DownloadFileByBatchId,
  CreateNewRequestForExistingBatch,
  CheckBatchDateOlderThan90Days,
} from "../../../components/pages/835RemittanceExporting/BatchIdDownloadHelper";
import {
  Base64ToArrayBuffer,
  SaveByteArray,
} from "shared/helpers/Base64ArrayTranslator";
import AdminExportBatchDetailModel from "./AdminExportBatchDetailModel";
import { useReactQueryHelpers } from "shared/hooks/useReactQueryHelpers";
import { useAdminExportDeleteBatchHistory } from "./hooks/useAdminExportDeleteBatchHistory";
import { LoadingSpinner } from "../../../components/layout/LoadingSpinner";


export function AdminExportBatchHistory(props) {
  const { passedProps } = props;
  const user = passedProps.user.email;
  const userConfig = useContext(UserContext);
  const [error, setError] = useState({});
  const { invalidateReactQueryKey } = useReactQueryHelpers();
  const [isLoading, setIsLoading] = useState(false);

  const [submittedFormData, setSubmittedFormData] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const { AddGridButton, gridButtons } = useGridButtons();
  const [selectedBatchId, setSelectedBatchId] = useState(null);
  const [refreshDetailGrid, setRefreshDetailGrid] = useState(false);
  let { mutate: mutateForAdminExportDeleteBatchHistory } = useAdminExportDeleteBatchHistory();

  const clientGridRef = useRef();
  const {
    gridDataStateResetProcessingInitiated,
    InitiateGridDataStateReset,
    GridDataStateResetCompleted: onGridDataStateResetCompleted,
  } = useGridDataStateResetProcessing();
  const batchIdKey = "batchId";

  const gridApiParams = useMemo(() => {
    if (IsNullOrEmpty(user)) {
      return {};
    }

    if (!submittedFormData) {
      return {};
    }

    let params = {
      user: user,
      fromDate: submittedFormData.fromDate
        ? GetISODate(submittedFormData.fromDate)
        : "",
      toDate: submittedFormData.toDate
        ? GetISODate(submittedFormData.toDate)
        : "",
      batchId: submittedFormData.batchId,
      batchStatus: submittedFormData.batchStatus,
      parentCompanyId:
        submittedFormData.selectedParentCompany === "" ||
          submittedFormData.selectedParentCompany === null
          ? null
          : submittedFormData.selectedParentCompany.value,
    };

    return params;
  }, [submittedFormData]);

  useEffect(() => {
    AddGridButton(
      "Delete Selected Batches",
      handleDeleteSelectedBatches, true
    );
  }, []);

  const handleDeleteSelectedBatches = async (selectablesFromSharedGrid) => {
    const allDetailsselectedsClaims = clientGridRef.current.GetSelectedGridData();
    let arrayOfBatchIds = allDetailsselectedsClaims.map((t) => parseInt(t.batchId));
    
    if (arrayOfBatchIds.length > 0) {
      deleteBatchHistory(arrayOfBatchIds);
    }
  };

  function deleteBatchHistory(arrayOfBatchIds) {
    let mutateParameter = {
      user: user,
      batchIds: arrayOfBatchIds,
      callback: () => {
        setRefreshDetailGrid(refreshDetailGrid ? false : true);
        invalidateReactQueryKey("api/RemittanceExport/GetRemittanceBatchHistory");
      },
    };

    mutateForAdminExportDeleteBatchHistory(mutateParameter);
  }

  function handleFormSubmit(e) {
    InitiateGridDataStateReset();
    setSubmittedFormData(e);
  }

  function handleDetailsModalClose() {
    setShowModal(false);
  }


  const onBatchSummaryCellClick = (dataItem) => {
    let batchId = dataItem[batchIdKey];

    checkDateAndBatchStatusAndRebuildOrDownloadBatch(dataItem, true);
  };

  const BatchSummaryCell = (props, optionalValue = null) => {
    let datum = props.dataItem;
    let batchId = datum[batchIdKey];

    if (batchId) {
      return (
        <td>
          <a onClick={() => onBatchSummaryCellClick(props.dataItem)}>
            {IsExportBatchRequestReadyToDownload(datum.status) ? (
              <i style={{ color: "green" }} className="fa fa-file-excel-o"></i>
            ) : null}
          </a>
        </td>
      );
    } else {
      return <td>{props.children}</td>;
    }
  };

  const BatchViewDetailsCell = (props) => {
    let batchId = props.dataItem[batchIdKey];

    if (batchId) {
      return (
        <td>
          <a onClick={() => onBatchViewDetailsClick(props.dataItem)}>
            <i style={{ color: "#015CAB" }} className="fa fa-search"></i>
          </a>
        </td>
      );
    } else {
      return <td>{props.children}</td>;
    }
  };

  const BatchIdCell = (props) => {
    let batchId = props.dataItem[batchIdKey];

    if (batchId) {
      return (
        <td>
          <a
            onClick={() => onBatchIdCellClick(props.dataItem)}
            style={{
              color: "#015CAB",
              cursor: "pointer",
              textDecoration: "underline",
            }}
          >
            {props.children}
          </a>
        </td>
      );
    } else {
      return <td>{props.children}</td>;
    }
  };

  const onBatchIdCellClick = (dataItem) => {
    let batchId = dataItem[batchIdKey];

    checkDateAndBatchStatusAndRebuildOrDownloadBatch(dataItem, false);
  };

  const checkDateAndBatchStatusAndRebuildOrDownloadBatch = (
    dataItem,
    isBatchSummary = false
  ) => {
    if (IsExportBatchRequestReadyToDownload(dataItem.status)) {
      if (CheckBatchDateOlderThan90Days(dataItem)) {
        let requestNewBuild = window.confirm(
          "Batch does not exist after 90 days.  Do you want to rebuild selected Batch?"
        );

        if (requestNewBuild) {
          let batchId = dataItem[batchIdKey];
          let accessToken = passedProps.auth.getAccessToken();
          let parameters = {
            user: user,
            batchId: batchId,
          };

          CreateNewRequestForExistingBatch(
            setIsLoading,
            handleSuccessNewRequestForRebuildingBatch,
            onFailure,
            parameters,
            accessToken
          );
        }
      } else {
        let batchId = dataItem[batchIdKey];
        let accessToken = passedProps.auth.getAccessToken();
        let parameters = {
          user: user,
          contactType: userConfig.contactType,
          batchId: batchId,
          isSummaryFile: isBatchSummary,
        };

        DownloadFileByBatchId(
          setIsLoading,
          handleSuccessGetFileByBatchId,
          onFailure,
          parameters,
          accessToken
        );
      }
    } else {
      alert(
        "This batch request is still pending, the download is not ready yet."
      );
    }
  };

  const handleSuccessNewRequestForRebuildingBatch = (response) => {
    setIsLoading(false);
  };

  const handleSuccessGetFileByBatchId = (response) => {
    //setParentIsLoading(false);
    setIsLoading(false);

    if (response.data) {
      var dataAsArray = Base64ToArrayBuffer(response.data.fileStream);
      SaveByteArray(response.data.fileName, dataAsArray);
    }
  };

  const onBatchViewDetailsClick = (batchRequest) => {
    setSelectedBatchId(batchRequest[batchIdKey]);
    setShowModal(true);
  };

  function RequestsDataProcessor(responseData) {
    let dataWithCleanedDates = responseData.map((t) => {
      return {
        ...t,
        batchRequestDate: IsNullOrWhitespace(t.batchRequestDate)
          ? ""
          : StringToDate(t.batchRequestDate).toLocaleString(),
        batchCreationDate: IsNullOrWhitespace(t.batchCreationDate)
          ? ""
          : StringToDate(t.batchCreationDate).toLocaleString(),

      };
    });
    return dataWithCleanedDates;
  }

  function onFailure(error) {
    setIsLoading(false);

    if (error.response) {
      switch (error.response.config.url) {
        case "api/RemittanceExport/Get":
          setError(error);
          break;
        case "api/RemittanceExport/CreateNewRequestForAllUnexpectedRemittances":
          setError(error);
          break;
        case "api/RemittanceExport/GetFileByBatchId":
          setError(error);
          break;
        case "api/RemittanceExport/GetRemittanceBatchHistory":
          setError(error);
          break;
        case "api/RemittanceExport/GetBatchDetails":
          setError(error);
          break;
        case "api/RemittanceExport/DeleteBatches":
          setError(error);
          break;
        default:
          break;
      }
    } else {
      console.log(error);
    }
  }

  return (
    <React.Fragment>
      <div className="feature" id="admin-request-exports">
        <ErrorHandler
          error={error}
          onClose={() => {
            setError({});
          }}
        />
         <LoadingSpinner
                isDataLoading={isLoading}
                controlsName={"admin-request-exports"}
              />

        <Accordion defaultExpanded label="Search & Filter">
          <Row>
            <Col className="higher-zindex-filters">
              <AdminExportBatchHistoryForm
                handleFormSubmit={handleFormSubmit}
              />
            </Col>
          </Row>
        </Accordion>
        <Row>
          <Col className="tight-grid multi-line-filter">
            <h4>Export Batch History Detail</h4>
            <SharedMainSelectableClientGrid
              ref={clientGridRef}
              isSelectable={true}
              apiGetEndpoint={"api/RemittanceExport/GetRemittanceBatchHistory"}
              apiParameters={gridApiParams}
              dataItemKey={"batchId"}
              exportFileNamePrefix={"AdminExportBatchHistory"}
              gridButtons={gridButtons}
              gridDataStateResetProcessingInitiated={
                gridDataStateResetProcessingInitiated
              }
              onGridDataStateResetCompleted={onGridDataStateResetCompleted}
              refreshGrid={refreshDetailGrid}
              dateFormattingColumns={["checkDate"]}
              onDataRetrievedProcessor={RequestsDataProcessor}
            >
              <GridColumn
                key={"batchId"}
                field="batchId"
                title="Batch ID"
                width={DefaultBooleanOrShortWidthColumnWidth()}
                cells={{
                  data: BatchIdCell,
                }}
              />
              <GridColumn
                key={"batchSummary"}
                field="batchSummary"
                title="Batch Summary"
                width={125}
                cells={{
                  data: BatchSummaryCell,
                }}
              />
              <GridColumn
                key={"batchCheckDetails"}
                field="batchCheckDetails"
                title="View Details"
                width={DefaultBooleanOrShortWidthColumnWidth()}
                cells={{
                  data: BatchViewDetailsCell,
                }}
              />
              <GridColumn
                key={"parentCompanyName"}
                field="parentCompanyName"
                title="Parent Company"
                width={DefaultColumnWidth()}
              />
              <GridColumn
                key={"batchRequestDate"}
                field="batchRequestDate"
                title="Batch Request Date"
                width={DefaultDateColumnWidth()}
              />
              <GridColumn
                key={"batchCreationDate"}
                field="batchCreationDate"
                title="Batch Creation Date"
                width={DefaultDateColumnWidth()}
              />
              <GridColumn key={"status"} field="status" title="Status" width={DefaultDateColumnWidth()} />
              <GridColumn
                key={"requestedByUser"}
                field="requestedByUser"
                title="Requested By"
                width={DefaultColumnWidth()}
              />
              <GridColumn
                key={"downloadedByUser"}
                field="downloadedByUser"
                title="Downloaded By"
                width={DefaultColumnWidth()}
              />
            </SharedMainSelectableClientGrid>
          </Col>
        </Row>

      </div>

      <DetailsModal
        title="Batch Details"
        show={showModal}
        handleClose={() => handleDetailsModalClose()}
      >
        <ZIndexContext.Provider value={10003}>
          <AdminExportBatchDetailModel
            batchId={selectedBatchId}
            user={user}
            contactType={userConfig.contactType}
            getAccessToken={() => passedProps.auth.getAccessToken()}
          />
        </ZIndexContext.Provider>
      </DetailsModal>
    </React.Fragment>
  );
}